/**
 * Generic Costing Page
 *
 * Universal costing interface supporting all commodities across F&B and BPCH towers.
 * Dynamically configures based on tower/commodity parameters from URL.
 *
 * Based on CookiesPage.jsx structure (proven working implementation).
 */

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Dialog,
  DialogContent,
  Paper,
} from "@mui/material";
import SidebarLayout from "../sections/sidebar/SidebarLayout";
import TitleSection from "../sections/TitleSection";
import LeftNavigation from "../sections/NavigationSection";
import InfoNotification from "../modules/InfoNotification";
import QuickCostButton from "../modules/QuickCostButton";
import CircularLoading from "../modules/CircularLoading";
import FormulationTable from "../modules/FormulationTable";
import ResultsSection from "../sections/ResultsSection";
import ProgressiveAnalysisTracker from "../../features/fb/cookies/ProgressiveAnalysisTracker";
import { CookiesQuestions } from "../../features/fb/cookies/questions";
import { CookiesPlayBook } from "../../features/fb/cookies/playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { theme } from "../../design/theme";

import {
  getFunctionalGroups,
  getTowerConfig,
  getCommodityPlaceholder,
} from "../../utils/commodityConfig";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";

const { perlColors, perlTypography } = theme.custom;

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "packaging", "ohp"];
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => {
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Assumptions rendering component
function renderAssumptions({ assumptions = {} }) {
  return (
    <Box>
      {assumptions && Object.keys(assumptions).length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "18px",
              fontFamily: perlTypography.family,
              letterSpacing: "0.3px",
              color: perlColors.textPrimary,
            }}
          >
            Assumptions
          </Typography>

          {/* Render each subsection if available */}
          {assumptions.Formulation && assumptions.Formulation.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Formulation
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Formulation.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Process && assumptions.Process.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Process
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Process.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Packaging && assumptions.Packaging.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Packaging
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Packaging.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.OHP && assumptions.OHP.length > 0 && (
            <Box sx={{ mb: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                OHP
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.OHP.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default function GenericCostingPage() {
  const [searchParams] = useSearchParams();
  const tower = searchParams.get("tower") || "FB";
  const commodity = searchParams.get("commodity") || "Cookies";
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  // Get tower-specific configuration
  const towerConfig = useMemo(() => getTowerConfig(tower), [tower]);
  const functionalGroups = useMemo(() => getFunctionalGroups(tower, commodity), [tower, commodity]);

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [assumptionsData, setAssumptionsData] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const previousRunKeyRef = React.useRef(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Track which section was last modified
  const [lastModifiedSection, setLastModifiedSection] = useState(null);

  // Auto-enable sections when data is populated
  React.useEffect(() => {
    if (playBookData && Object.keys(playBookData).length > 0) {
      setSectionStarted((prev) => ({ ...prev, playbook: true }));
    }
  }, [playBookData]);

  React.useEffect(() => {
    if (recipeData && recipeData.length > 0) {
      setSectionStarted((prev) => ({ ...prev, formulation: true }));
    }
  }, [recipeData]);

  React.useEffect(() => {
    if (costData) {
      setSectionStarted((prev) => ({ ...prev, results: true }));
    }
  }, [costData]);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
    setLastModifiedSection("product");
  };

  const handlePlayBookChange = (field, value) => {
    setPlayBookData({ ...playBookData, [field]: value });
    setLastModifiedSection("playbook");
  };

  const getTimestampId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  const createGuid = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const rand = Math.random() * 16;
      const value = char === "x" ? rand : (rand & 0x3) | 0x8;
      return Math.floor(value).toString(16);
    });
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please complete product information first");
      return;
    }

    setDebugStoredProcedureSql("");
    setLatestProjectId("");
    setSqlCopyStatus("idle");

    console.log(`🚀 Starting ${tower} ${commodity} Progressive Quick Cost Analysis with SSE...`);

    // Initialize progress steps
    const totalSteps = 14;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);

    let maxProgress = 0;

    try {
      const timestamp = getTimestampId();

      const payloadBase = {
        tower: tower,
        commodity: commodity,
        userEmail: user?.email || "",
        productName: productData.productName,
        country: productData.manufacturingCountry,
        skuWeight: parseFloat(productData.skuWeight),
        skuWeightUnit: productData.skuWeightUnit,
        marketPositioning: productData.marketPositioning,
        annualVolume: productData.annualVolume,
      };

      const runKeyString = JSON.stringify(payloadBase);
      const isReRun = previousRunKeyRef.current === runKeyString;

      const normalizedRecipe = Array.isArray(recipeData)
        ? recipeData.filter((row) => row && (row.ingredient || row.percentage))
        : [];

      const generatedProjectId = createGuid();
      const payload = {
        ...payloadBase,
        primaryPackaging: playBookData?.primary_packaging || "",
        existingRecipe: normalizedRecipe.length > 0 ? normalizedRecipe : undefined,
        isReRun: isReRun,
        // projectId: `PROJ_${1764775036995}`,
        projectId: generatedProjectId,
        partKey: `PART_${1764775036995}`,
        componentId: `Comp1`,
      };
      setLatestProjectId(generatedProjectId);

      console.log(
        `📤 Sending payload to ${towerConfig.apiPrefix}/execute-complete-analysis-stream:`,
        payload,
      );

      const response = await apiFetch(`${towerConfig.apiPrefix}/execute-complete-analysis-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;

          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📩 SSE Event:", eventData);

            if (eventData.type === "start") {
              const totalSteps = eventData.total_steps || 14;
              if (totalSteps !== progressSteps.length) {
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              const stepIndex = eventData.step - 1;
              console.log(
                `📊 Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`,
              );

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: eventData.label || updated[stepIndex].label,
                    status: mappedStatus,
                    message:
                      eventData.message ||
                      (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };
                }
                return updated;
              });

              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              console.log("✅ Analysis complete:", eventData);
              previousRunKeyRef.current = runKeyString;
              maxProgress = 100;
              setOverallProgress(100);

              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                })),
              );

              // Extract and unwrap data from response
              if (eventData.RecipeData) {
                console.log("🍪 Setting recipe data");
                setRecipeData(eventData.RecipeData);
              }

              if (eventData.playBookData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedPlayBookData =
                  eventData.playBookData.playBookData || eventData.playBookData;
                console.log("🎯 Setting playbook data:", unwrappedPlayBookData);
                console.log("🔍 Playbook data keys:", Object.keys(unwrappedPlayBookData));
                console.log("🔍 Has primary_packaging?", unwrappedPlayBookData.primary_packaging);
                console.log(
                  "🔍 Has secondary_packaging?",
                  unwrappedPlayBookData.secondary_packaging,
                );
                console.log("🔍 Has tertiary_packaging?", unwrappedPlayBookData.tertiary_packaging);
                setPlayBookData(unwrappedPlayBookData);
                setSectionStarted((prev) => ({ ...prev, playbook: true }));
              }

              if (eventData.summary) {
                setCostData({
                  breakdown: {
                    material: eventData.summary.material_cost,
                    process: eventData.summary.process_cost,
                    packaging: eventData.summary.packaging_cost,
                    ohp: eventData.summary.ohp_cost,
                    total_should_cost: eventData.summary.total_cost,
                  },
                });
              }

              if (eventData.ShouldCostResult) {
                console.log("💰 Setting cost data");
                const costData = {
                  breakdown: {
                    material: eventData.ShouldCostResult.MaterialCost || 0,
                    process: eventData.ShouldCostResult.ProcessCost || 0,
                    packaging: eventData.ShouldCostResult.PackagingCost || 0,
                    ohp: eventData.ShouldCostResult.OHPCost || 0,
                  },
                  total: eventData.ShouldCostResult.TotalCost || 0,
                };
                setCostData(costData);
              }

              if (eventData.riskData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedRiskData = eventData.riskData.riskData || eventData.riskData;
                console.log("⚠️ Setting risk data:", unwrappedRiskData);
                console.log("🔍 Risk data structure:", {
                  hasMatrix: !!unwrappedRiskData.matrix,
                  hasSummary: !!unwrappedRiskData.summary,
                  hasRiskItems: !!unwrappedRiskData.risk_items,
                  keys: Object.keys(unwrappedRiskData),
                });
                setRiskData(unwrappedRiskData);
              }

              if (eventData.substituteData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedSubstituteData =
                  eventData.substituteData.substituteData || eventData.substituteData;
                console.log("🔄 Setting substitute data:", unwrappedSubstituteData);
                console.log("🔍 Substitute data structure:", {
                  hasSummary: !!unwrappedSubstituteData.summary,
                  hasIdeas: !!unwrappedSubstituteData.ideas,
                  keys: Object.keys(unwrappedSubstituteData),
                });
                setSubstituteData(unwrappedSubstituteData);
              }

              if (eventData.productImage?.imageUrl) {
                console.log("🖼️ Setting product image URL:", eventData.productImage.imageUrl);
                setProductImageUrl(eventData.productImage.imageUrl);
              } else {
                console.log("⚠️ No product image URL in response");
              }

              if (eventData.assumptions) {
                console.log("📝 Setting assumptions data:", eventData.assumptions);
                console.log("   Assumptions sections:", Object.keys(eventData.assumptions));
                setAssumptionsData(eventData.assumptions);
              }

              if (eventData.KeyInsightsJson) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedKeyInsights =
                  eventData.KeyInsightsJson.key_insights || eventData.KeyInsightsJson;
                console.log("🔑 Setting key insights data:", unwrappedKeyInsights);
                console.log("   Key insights keys:", Object.keys(unwrappedKeyInsights));
                console.log("   Has cost_savings?", !!unwrappedKeyInsights.cost_savings);
                console.log("   Has strategic?", !!unwrappedKeyInsights.strategic);
                setKeyInsightsData(unwrappedKeyInsights);
              }

              setSectionStarted((prev) => ({ ...prev, results: true }));

              setTimeout(() => {
                setShowProgressTracker(false);
                setActiveSection("results");
              }, 1500);
            } else if (eventData.type === "error") {
              console.error("❌ Error:", eventData.message);
              alert(`Analysis failed: ${eventData.message}`);
              setShowProgressTracker(false);
            }
          } catch (e) {
            console.error("Failed to parse SSE event:", e);
          }
        }
      }
    } catch (error) {
      console.error("❌ Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setShowProgressTracker(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description: `Answer a few quick questions about your ${commodity.toLowerCase()} product to generate an AI-driven cost analysis with formulation, manufacturing parameters, and detailed insights.`,
    },
    {
      id: "formulation",
      label: "Formulation",
      icon: <ScienceIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Build Formulation",
      description: "View and edit ingredient composition with functional groups and percentages.",
    },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);

  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total ?? costData?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  const hasResultsData =
    !!costData ||
    !!riskData ||
    !!substituteData ||
    !!productImageUrl ||
    !!assumptionsData ||
    !!keyInsightsData;

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName={towerConfig.name}
          industry={towerConfig.industry}
          commodity={commodity}
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height: "92%" }}>
                <CookiesQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                  commodity={commodity}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor={towerConfig.brandColor}
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <CookiesPlayBook
                    data={playBookData}
                    onChange={handlePlayBookChange}
                    tower={tower}
                    commodity={commodity}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate formula first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {hasResultsData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor={towerConfig.brandColor}
                    assumptions={assumptionsData || {}}
                    keyInsights={keyInsightsData || null}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: assumptionsData || {},
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleProgressiveQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor={towerConfig.brandColor}
          />
        )}

        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
